import { AnalyticsEvent } from '@cibli/shared';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainLayout from '@/features/common/components/MainLayout';
import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { usePaymentWebSocket } from '@/hooks/usePaymentWebSocket';
import { useTrackEvent } from '@/lib/analytics/use-track-event';
import { cancelReaderAction, collectPaymentIntent, createPaymentIntent } from '@/services/payment';

import CancelProcessingButton from '../components/payment/CancelProcessingButton';
import DevPaymentDebugPanel from '../components/payment/DevPaymentDebugPanel';
import ErrorStateActions from '../components/payment/ErrorStateActions';
import ValidatedPaymentScreen from './ValidatedPaymentScreen';

interface Props {
  onValidate: (paymentIntentId: string) => void;
  onBack: () => void;
}

type PaymentState = 'idle' | 'collecting' | 'processing' | 'succeeded' | 'error' | 'cancelling';

export default function ProceedToPaymentScreen({ onValidate, onBack }: Props) {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { interview } = useInterviewContext();
  const { booth } = useBooth();
  const terminalId = booth?.terminalId;
  const trackEvent = useTrackEvent();
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  const [paymentState, setPaymentState] = useState<PaymentState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [lastRetryAtIso, setLastRetryAtIso] = useState<string | null>(null);

  const {
    subscribeToPaymentIntent,
    unsubscribeFromPaymentIntent,
    subscribeToReader,
    unsubscribeFromReader,
    paymentIntents,
    readerStates,
    isConnected,
    subscribedReaders,
  } = usePaymentWebSocket();

  // Get the current payment intent state from the existing WebSocket state map
  const paymentIntentState = useMemo(
    () => (paymentIntentId ? paymentIntents.get(paymentIntentId) : undefined),
    [paymentIntentId, paymentIntents],
  );

  // Get the current reader state
  const readerState = terminalId ? readerStates.get(terminalId) : undefined;

  // Use refs for flags that don't need to trigger re-renders
  const isCreatingPaymentIntentRef = useRef(false);
  const isCollectingRef = useRef(false);

  // Create payment intent - single source of truth
  const localCreatePaymentIntent = useCallback(async () => {
    // Prevent multiple simultaneous creations
    if (isCreatingPaymentIntentRef.current || paymentIntentId) {
      return;
    }

    isCreatingPaymentIntentRef.current = true;
    try {
      const response = await createPaymentIntent();
      if (response.paymentIntentId) {
        setPaymentIntentId(response.paymentIntentId);
        return response.paymentIntentId;
      } else {
        setPaymentState('error');
        setErrorMessage(t('errors.reader_internal_error'));
        return null;
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setPaymentState('error');
      setErrorMessage(t('errors.reader_internal_error'));
      return null;
    } finally {
      isCreatingPaymentIntentRef.current = false;
    }
  }, [t, paymentIntentId]);

  // Wrapper to reset payment intent ID when going back
  const handleBack = useCallback(() => {
    setPaymentIntentId(null);
    onBack();
  }, [onBack]);

  const handleCollectPaymentIntent = useCallback(async () => {
    // Prevent multiple simultaneous collections
    if (isCollectingRef.current || !terminalId || !paymentIntentId) {
      if (!terminalId || !paymentIntentId) {
        setPaymentState('error');
        setErrorMessage(t('errors.reader_internal_error'));
      }
      return;
    }

    // Only collect if we're in idle state
    if (paymentState !== 'idle') {
      return;
    }

    isCollectingRef.current = true;
    setErrorMessage(undefined);
    setLastRetryAtIso(new Date().toISOString());

    try {
      setPaymentState('collecting');

      // Subscribe to payment intent updates and wait for confirmation
      await subscribeToPaymentIntent(paymentIntentId);

      // Send collect payment intent request
      const resp = await collectPaymentIntent(terminalId, paymentIntentId);

      if (!resp.success) {
        setPaymentState('error');
        if (i18n.exists(`errors.${resp.code}`)) {
          setErrorMessage(t(`errors.${resp.code}`));
        } else {
          setErrorMessage(t('errors.reader_internal_error'));
        }
        return;
      }

      setPaymentState('processing');
    } catch (error) {
      console.error('Error collecting payment intent:', error);
      setPaymentState('error');
      setErrorMessage(t('errors.reader_internal_error'));
    } finally {
      isCollectingRef.current = false;
    }
  }, [terminalId, paymentIntentId, subscribeToPaymentIntent, t, i18n, paymentState]);

  const handleCancelPaymentIntent = useCallback(async () => {
    if (!terminalId) {
      return;
    }

    setPaymentState('cancelling');
    setErrorMessage(undefined);

    setIsLoading(true);

    try {
      await cancelReaderAction(terminalId);
      setPaymentIntentId(null);
      onBack();
    } catch (error) {
      console.error('Error canceling payment intent:', error);
    } finally {
      setPaymentState('idle');
      setIsLoading(false);
    }
  }, [onBack, terminalId, setPaymentIntentId]);

  // Monitor payment intent state changes
  useEffect(() => {
    if (!paymentIntentState) return;

    // Ignore stale updates that occurred before the latest retry
    if (lastRetryAtIso) {
      const updateTime = new Date(paymentIntentState.timestamp).getTime();
      const retryTime = new Date(lastRetryAtIso).getTime();
      if (!Number.isNaN(updateTime) && !Number.isNaN(retryTime) && updateTime < retryTime) {
        return;
      }
    }

    switch (paymentIntentState.status) {
      case 'created':
        setPaymentState('processing');
        break;
      case 'authorized':
        // Authorized means the card-present transaction is approved and amount is capturable
        setPaymentState('succeeded');
        if (paymentIntentId) {
          trackEvent({
            event: AnalyticsEvent.PAYMENT_SUCCEEDED,
            props: { payment_intent_id: paymentIntentId },
          });
          setTimeout(() => {
            onValidate(paymentIntentId);
          }, 1000);
        }
        break;
      case 'failed':
        setPaymentState('error');
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentState, onValidate, lastRetryAtIso, readerState]);

  // Create payment intent when ready
  useEffect(() => {
    // Only create if we're in idle state and don't have a paymentIntentId yet
    if (paymentState !== 'idle' || paymentIntentId || isCreatingPaymentIntentRef.current) {
      return;
    }

    // Need terminal and WebSocket connection
    if (!terminalId || !isConnected) {
      return;
    }

    // Check if reader is subscribed
    const isReaderSubscribed = subscribedReaders.includes(terminalId);
    if (!isReaderSubscribed) {
      return;
    }

    localCreatePaymentIntent();
  }, [
    paymentIntentId,
    paymentState,
    isConnected,
    subscribedReaders,
    terminalId,
    localCreatePaymentIntent,
  ]);

  // Start collection when payment intent is ready
  useEffect(() => {
    // Only collect if we're in idle state and have a paymentIntentId
    if (paymentState !== 'idle' || !paymentIntentId || isCollectingRef.current) {
      return;
    }

    // Need terminal and WebSocket connection
    if (!terminalId || !isConnected) {
      return;
    }

    // Check if reader is subscribed
    const isReaderSubscribed = subscribedReaders.includes(terminalId);
    if (!isReaderSubscribed) {
      return;
    }

    handleCollectPaymentIntent();
  }, [
    paymentIntentId,
    paymentState,
    handleCollectPaymentIntent,
    isConnected,
    subscribedReaders,
    terminalId,
  ]);

  // Subscribe to reader state updates - do this first and immediately
  useEffect(() => {
    if (!terminalId) return;
    subscribeToReader(terminalId);

    return () => {
      unsubscribeFromReader(terminalId);
    };
  }, [terminalId, subscribeToReader, unsubscribeFromReader]);

  // Cleanup subscription on unmount or when payment is complete
  useEffect(() => {
    return () => {
      if (paymentIntentId) {
        unsubscribeFromPaymentIntent(paymentIntentId);
      }
    };
  }, [paymentIntentId, unsubscribeFromPaymentIntent]);

  // If interview already has a paymentIntentId, show ValidatedPayment instead
  if (interview?.paymentIntentId) {
    return (
      <ValidatedPaymentScreen onValidate={onValidate} paymentIntentId={interview.paymentIntentId} />
    );
  }

  return (
    <MainLayout className="gap-12 sm:gap-20">
      <PaymentStateIcon state={paymentState} />

      <div className="space-y-2 text-center max-w-md">
        <h3 className="text-lg font-semibold text-gray-900">
          {t(`FormScreen.ProceedToPayment.status.${paymentState}`)}
        </h3>
        <p className="text-sm text-gray-600">
          {errorMessage || t(`FormScreen.ProceedToPayment.message.${paymentState}`)}
        </p>
      </div>

      {import.meta.env.DEV && (
        <DevPaymentDebugPanel
          paymentIntentId={paymentIntentId}
          isConnected={isConnected}
          readerState={readerState}
        />
      )}

      {paymentState === 'error' && (
        <ErrorStateActions
          onRetry={() => {
            // Reset state to allow useEffect to create a new payment intent and retry
            setPaymentIntentId(null);
            setPaymentState('idle');
            setErrorMessage(undefined);
            isCollectingRef.current = false;
            isCreatingPaymentIntentRef.current = false;
          }}
          onBack={handleBack}
        />
      )}

      {paymentState === 'processing' && (
        <CancelProcessingButton isLoading={isLoading} onCancel={handleCancelPaymentIntent} />
      )}
    </MainLayout>
  );
}

function PaymentStateIcon({ state }: { state: PaymentState }) {
  switch (state) {
    case 'idle':
    case 'cancelling':
    case 'collecting':
    case 'processing':
      return <Loader2 className="size-8 animate-spin" />;
    case 'succeeded':
      return <CheckCircle className="size-8 text-green-600" />;
    case 'error':
      return <XCircle className="size-8 text-red-600" />;
    default:
      return null;
  }
}
